import { AxiosError } from "axios";
import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { getErrorMessage } from "@/core/api/errors";
import { trackReferralUpdate } from "./events";
import { useDeleteReferral, useUpdateReferral } from "./query";
import type { IReferral, ReferralUpdateParams } from "./referral";

export const useReferralActions = (
  businessId: string | null | undefined,
  selectedReferral: IReferral | null,
) => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const { mutate: updateReferral } = useUpdateReferral(businessId);
  const { mutate: deleteReferral } = useDeleteReferral(businessId);

  const handleSidebarClose = useCallback(() => {
    navigate("/referrals", { replace: true });
  }, [navigate]);

  const handleReferralUpdate = useCallback(
    ({
      referral,
      updates,
      method,
      onSuccessCallback,
    }: {
      referral: IReferral;
      updates: Partial<ReferralUpdateParams>;
      method: "sidebar" | "modal" | "drag_and_drop";
      onSuccessCallback?: (data: IReferral) => void;
    }) => {
      updateReferral(
        { id: referral.id, updates },
        {
          onSuccess: (data) => {
            if (updates.status === "LOST") {
              if (selectedReferral?.id === referral.id) {
                handleSidebarClose();
              }
              toast.success("Referral marked as lost");
            } else {
              toast.success("Referral status updated successfully");
            }
            trackReferralUpdate(posthog, {
              referral,
              updates,
              method,
              success: true,
            });
            if (selectedReferral?.id === data.id) {
              navigate(`/referrals?selected=${data.id}`, { replace: true });
            }
            onSuccessCallback?.(data);
          },
          onError: (error) => {
            trackReferralUpdate(posthog, {
              referral,
              updates,
              method,
              success: false,
              error,
            });
            let title = "Failed to update referral status";
            if (error instanceof AxiosError) {
              title = Array.isArray(error.response?.data?.error) ? (
                <Stack align="start" gap={2}>
                  {error.response?.data?.error.map((e: string) => (
                    <Text variant="caption" tone="inverse" key={e}>
                      {e}
                    </Text>
                  ))}
                </Stack>
              ) : (
                error.response?.data?.error || title
              );
            }
            toast.error(title, {
              description:
                title === "Failed to update referral status"
                  ? getErrorMessage(error)
                  : "Try again.",
            });
          },
        },
      );
    },
    [updateReferral, selectedReferral, posthog, navigate, handleSidebarClose],
  );

  const handleUpdateReferral = useCallback(
    (values: Partial<ReferralUpdateParams>, referral: IReferral) => {
      handleReferralUpdate({
        referral,
        updates: values,
        method: "sidebar",
      });
    },
    [handleReferralUpdate],
  );

  const handleDeleteReferral = useCallback(
    (referral: IReferral) => {
      deleteReferral(
        { id: referral.id },
        {
          onSuccess: () => {
            if (selectedReferral?.id === referral.id) {
              handleSidebarClose();
            }
            toast.success("Referral deleted successfully");
          },
          onError: (error) => {
            toast.error("Failed to delete referral", {
              description: getErrorMessage(error),
            });
          },
        },
      );
    },
    [deleteReferral, selectedReferral, handleSidebarClose],
  );

  return {
    handleSidebarClose,
    handleReferralUpdate,
    handleUpdateReferral,
    handleDeleteReferral,
  };
};
